import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useLearnerContext } from "../data/context/LearnerContext";
import JobOpportunityClient, { JobOpportunityListView } from "../../jobOpportunity/data/api/JobOpportunityClient";
import JobApplicationClient from "../../jobApplications/data/api/JobApplicationClient";
import LearnerClient from "../data/api/LearnerClient";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "../../../shared/components/Table/Table";
import { Link2, Globe, Briefcase } from "lucide-react";
import Loader from "../../../shared/components/Loader";
import Alert from "../../../shared/components/Alert";
import Modal from "../../../shared/components/Modal";
import StatusFilterDropdown from "../components/StatusFilterDropdown";
import {
  BulkResumeUploadRecord,
  ResumeUploadItem,
} from "../data/interfaces/LearnerInterfaces";
import "../styles/LearnerStyles.css";

type UploadMode = "excel" | "resume";

const APPLICANT_SOURCES = [
  "LinkedIn", "Referral", "Job portal", "Website", "Agency", "Walk-in", "Internal", "Other"
];

const RESUME_STATUS_FILTER_OPTIONS = [
  { value: "processing", label: "Processing", dotColor: "#f59e0b" },
  { value: "parsing", label: "Parsing", dotColor: "#8b5cf6" },
  { value: "complete", label: "Complete", dotColor: "#059669" },
  { value: "failed", label: "Failed", dotColor: "#ef4444" },
];

const UploadLearnersPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnUrl = location.state?.returnTo || "/dashboard/learners";
  const { state, dispatch } = useLearnerContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const resumeFileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showTemplateOptions, setShowTemplateOptions] = useState(false);
  const templateRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [mode, setMode] = useState<UploadMode>("resume");

  // View Modal state (Excel)
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewRecords, setViewRecords] = useState<any[]>([]);
  const [viewLoading, setViewLoading] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [viewSearchQuery, setViewSearchQuery] = useState("");

  // Resume Upload state
  const [selectedResumes, setSelectedResumes] = useState<File[]>([]);
  const [resumeDragOver, setResumeDragOver] = useState(false);
  const [resumeUploading, setResumeUploading] = useState(false);
  const [resumeRecords, setResumeRecords] = useState<BulkResumeUploadRecord[]>(
    [],
  );
  const [resumeRecordsLoading, setResumeRecordsLoading] = useState(false);
  const [resumeStatusFilter, setResumeStatusFilter] = useState("all");
  const [resumeSearchQuery, setResumeSearchQuery] = useState("");
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  const [resumePage, setResumePage] = useState(1);
  const [resumeHasMore, setResumeHasMore] = useState(true);

  // Resume Upload Detail Modal
  const [resumeDetailOpen, setResumeDetailOpen] = useState(false);
  const [resumeDetailData, setResumeDetailData] = useState<ResumeUploadItem[]>(
    [],
  );
  const [resumeDetailLoading, setResumeDetailLoading] = useState(false);

  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobs, setJobs] = useState<any[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<string>('');
  const [selectedSource, setSelectedSource] = useState<string>('Other');

  useEffect(() => {
    const fetchJobs = async () => {
      setJobsLoading(true);
      try {
        const data = await JobOpportunityClient.listOpportunities({ page_number: 1, limit: 1000, status: 'Open' });
        setJobs(data.records || []);
      } catch (err) {
        console.error("Failed to load jobs", err);
      } finally {
        setJobsLoading(false);
      }
    };
    fetchJobs();
  }, []);

  const [selectedResumeFileName, setSelectedResumeFileName] = useState("");
  const [selectedResumeFileId, setSelectedResumeFileId] = useState("");

  const closeAlert = () => dispatch({ type: "SET_ERROR", payload: null });

  // ── Excel Upload ──
  const handleFileSelect = async (file: File) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("files", file);
      if (selectedJobId) formData.append("job_opportunity_id", selectedJobId);
      if (selectedSource) formData.append("source", selectedSource);
      await LearnerClient.upload(formData);
    } catch (err: any) {
      dispatch({
        type: "SET_ERROR",
        payload: err?.response?.data?.detail || "Upload failed",
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleDownloadTemplate = async (format: "excel" | "csv") => {
    setDownloading(true);
    try {
      const res = await LearnerClient.downloadTemplate(format);
      const fileUrl = res.data.file_path;
      const link = document.createElement("a");
      link.href = fileUrl;
      link.setAttribute("download", "");
      document.body.appendChild(link);
      link.click();
      link.remove();
      setShowTemplateOptions(false);
    } catch {
      dispatch({ type: "SET_ERROR", payload: "Failed to download template" });
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        templateRef.current &&
        !templateRef.current.contains(e.target as Node)
      ) {
        setShowTemplateOptions(false);
      }
    };
    if (showTemplateOptions) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showTemplateOptions]);

  // const fetchUploadRecords = useCallback(async () => {
  //   setLoadingRecords(true);
  //   try {
  //     const data = await LearnerClient.uploadTrack();
  //     dispatch({ type: "SET_UPLOAD_RECORDS", payload: data.records || [] });
  //   } catch {
  //     dispatch({
  //       type: "SET_ERROR",
  //       payload: "Failed to fetch upload records",
  //     });
  //   } finally {
  //     setLoadingRecords(false);
  //   }
  // }, [dispatch]);

  // useEffect(() => {
  //   fetchUploadRecords();
  // }, [fetchUploadRecords]);

  // ── Resume Upload ──
  const handleResumeFilesSelect = (files: FileList | null) => {
    if (!files) return;
    const pdfs = Array.from(files).filter((f) =>
      f.name.toLowerCase().endsWith(".pdf") || f.name.toLowerCase().endsWith(".docx"),
    );
    if (pdfs.length !== files.length) {
      dispatch({ type: "SET_ERROR", payload: "Only PDF or DOCX files are allowed" });
      return;
    }
    const combined = [...selectedResumes, ...pdfs].slice(0, 5);
    if (combined.length !== selectedResumes.length + pdfs.length) {
      dispatch({ type: "SET_ERROR", payload: "Maximum 5 resumes allowed" });
    }
    setSelectedResumes(combined);
  };

  const handleResumeFileInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    handleResumeFilesSelect(e.target.files);
    if (resumeFileInputRef.current) resumeFileInputRef.current.value = "";
  };

  const handleResumeDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setResumeDragOver(false);
    handleResumeFilesSelect(e.dataTransfer.files);
  };

  const removeResumeFile = (index: number) => {
    setSelectedResumes((prev) => prev.filter((_, i) => i !== index));
  };

  const handleResumeUpload = async () => {
    if (selectedResumes.length === 0) {
      dispatch({
        type: "SET_ERROR",
        payload: "Please select at least one resume file",
      });
      return;
    }
    setResumeUploading(true);
    try {
      const formData = new FormData();
      selectedResumes.forEach((file) => {
        formData.append("files", file);
      });
      if (selectedJobId) formData.append("job_opportunity_id", selectedJobId);
      if (selectedSource) formData.append("source", selectedSource);
      await LearnerClient.uploadResumes(formData);

      const numResumes = selectedResumes.length;
      const resumeText = numResumes === 1 ? "the resume" : `${numResumes} resumes`;
      if (selectedJobId) {
        setInfoMessage(`Successfully added ${numResumes} applicants to the job opportunity.`);
      } else {
        setInfoMessage(`JozAI is parsing ${resumeText}. Details will appear once complete.`);
      }
      setSelectedResumes([]);
      setResumePage(1);
      fetchResumeUploadRecords(1, false);
    } catch (err: any) {
      dispatch({
        type: "SET_ERROR",
        payload: err?.response?.data?.detail || "Resume upload failed",
      });
    } finally {
      setResumeUploading(false);
    }
  };

  const fetchResumeUploadRecords = useCallback(async (page: number = 1, isLoadMore: boolean = false) => {
    setResumeRecordsLoading(true);
    try {
      const data = await LearnerClient.resumeUploadTrack({ page_number: page, limit: 15 });
      const newRecords = data.records || [];
      if (isLoadMore) {
        setResumeRecords((prev) => {
          const existingIds = new Set(prev.map((r: any) => r.file_id));
          const uniqueNew = newRecords.filter((r: any) => !existingIds.has(r.file_id));
          return [...prev, ...uniqueNew];
        });
      } else {
        setResumeRecords(newRecords);
      }
      setResumeHasMore(newRecords.length === 15);
    } catch {
      dispatch({
        type: "SET_ERROR",
        payload: "Failed to fetch resume upload records",
      });
    } finally {
      setResumeRecordsLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    if (resumePage === 1) {
      fetchResumeUploadRecords(1, false);
    } else {
      fetchResumeUploadRecords(resumePage, true);
    }
  }, [resumePage, fetchResumeUploadRecords]);

  const observer = useRef<IntersectionObserver | null>(null);
  const lastResumeElementRef = useCallback(
    (node: any) => {
      if (resumeRecordsLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && resumeHasMore) {
          setResumePage((prev) => prev + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [resumeRecordsLoading, resumeHasMore],
  );

  const handleResumeViewStatus = async (row: BulkResumeUploadRecord) => {
    setSelectedResumeFileName(row.file_name);
    setSelectedResumeFileId(row.file_id);
    setResumeDetailOpen(true);
    setResumeDetailLoading(true);
    try {
      const data = await LearnerClient.resumeUploadStatus(row.file_id);
      setResumeDetailData(data.records || []);
    } catch (err: any) {
      dispatch({
        type: "SET_ERROR",
        payload:
          err?.response?.data?.detail || "Failed to fetch resume details",
      });
      setResumeDetailData([]);
    } finally {
      setResumeDetailLoading(false);
    }
  };

  const handleResumeDetailRefresh = async () => {
    if (!selectedResumeFileId || resumeDetailLoading) return;
    setResumeDetailLoading(true);
    try {
      const data = await LearnerClient.resumeUploadStatus(
        selectedResumeFileId,
      );
      setResumeDetailData(data.records || []);
    } catch (err: any) {
      dispatch({
        type: "SET_ERROR",
        payload:
          err?.response?.data?.detail || "Failed to refresh resume details",
      });
    } finally {
      setResumeDetailLoading(false);
    }
  };

  // ── Shared helpers ──
  const getTrackerPill = (status: string, note?: string) => {
    const s = status?.toLowerCase() || "";
    let pillClass = "lp-badge pending";
    if (s.includes("success") || s.includes("complete")) pillClass = "lp-badge active";
    else if (s.includes("fail") || s.includes("error")) pillClass = "lp-badge inactive";
    return (
      <span
        className={pillClass}
        style={{
          display: "inline-flex",
          padding: "4px 10px",
          borderRadius: "12px",
          alignItems: "center",
          gap: 6,
        }}
        title={note || ""}
      >
        <span className="lp-badge-dot" />
        {status}
        {s.includes("fail") || s.includes("error")
          ? (
            <span style={{ cursor: "help" }}>
              <svg
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </span>
          )
          : null}
      </span>
    );
  };

  const getResumeStatusPill = (status: string, errorMsg?: string) => {
    const s = status?.toLowerCase() || "";
    let pillClass = "lp-badge pending";
    if (s.includes("synced") || s.includes("created") || s.includes("parsed"))
      pillClass = "lp-badge active";
    else if (s.includes("skip") || s.includes("duplicate"))
      pillClass = "lp-badge review";
    else if (s.includes("fail"))
      pillClass = "lp-badge inactive";
    let label = status;
    if (s.includes("syncing")) label = "Processing";
    else if (s.includes("synced")) label = "Completed";
    return (
      <span
        className={pillClass}
        style={{
          display: "inline-flex",
          padding: "4px 10px",
          borderRadius: "12px",
        }}
        title={errorMsg || ""}
      >
        <span className="lp-badge-dot" />
        {label}
      </span>
    );
  };

  // ── Excel filter logic ──
  const records = (state.uploadRecords || []).filter((r: any) => {
    const matchSearch = searchQuery
      ? r.file_name?.toLowerCase().includes(searchQuery.toLowerCase())
      : true;
    let matchStatus = true;
    if (statusFilter !== "all") {
      const s = (r.status || "").toLowerCase();
      if (statusFilter === "success")
        matchStatus = s.includes("success") || s.includes("complete");
      else if (statusFilter === "processing")
        matchStatus = s.includes("process") || s.includes("pending");
      else if (statusFilter === "failed")
        matchStatus = s.includes("fail") || s.includes("error");
    }
    return matchSearch && matchStatus;
  });

  const successCount = records.filter((r: any) => {
    const s = (r.status || "").toLowerCase();
    return s.includes("success") || s.includes("complete");
  }).length;

  const processingCount = records.filter((r: any) => {
    const s = (r.status || "").toLowerCase();
    return s.includes("process") || s.includes("pending");
  }).length;

  const failedCount = records.filter((r: any) => {
    const s = (r.status || "").toLowerCase();
    return s.includes("fail") || s.includes("error");
  }).length;

  const handleViewStatus = async (row: any) => {
    setSelectedFileName(row.file_name);
    setViewSearchQuery("");
    setViewModalOpen(true);
    setViewLoading(true);
    try {
      const data = await LearnerClient.uploadView({
        file_id: row.file_id,
        limit: 100,
      });
      setViewRecords(data.records || []);
    } catch (err: any) {
      dispatch({
        type: "SET_ERROR",
        payload:
          err?.response?.data?.detail ||
          "Failed to fetch upload status details",
      });
      setViewRecords([]);
    } finally {
      setViewLoading(false);
    }
  };

  const filteredViewRecords = viewRecords.filter((r) => {
    if (!viewSearchQuery) return true;
    const search = viewSearchQuery.toLowerCase();
    const fullName = `${r.first_name || ""} ${r.last_name || ""}`.toLowerCase();
    const email = (r.email_address || "").toLowerCase();
    return fullName.includes(search) || email.includes(search);
  });

  // ── Resume filter logic ──
  const filteredResumeRecords = resumeRecords.filter((r) => {
    const matchSearch = resumeSearchQuery
      ? r.file_name?.toLowerCase().includes(resumeSearchQuery.toLowerCase())
      : true;
    let matchStatus = true;
    if (resumeStatusFilter !== "all") {
      const s = (r.status || "").toLowerCase();
      if (resumeStatusFilter === "processing")
        matchStatus = s.includes("upload") || s.includes("process");
      else if (resumeStatusFilter === "parsing")
        matchStatus = s.includes("pars");
      else if (resumeStatusFilter === "complete")
        matchStatus = s.includes("complete") || s.includes("success");
      else if (resumeStatusFilter === "failed")
        matchStatus = s.includes("fail") || s.includes("error");
    }
    return matchSearch && matchStatus;
  });

  return (
    <>
      <Loader
        isLoading={
          uploading ||
          loadingRecords ||
          downloading ||
          resumeUploading ||
          resumeRecordsLoading
        }
        message={
          uploading
            ? "Uploading file..."
            : resumeUploading
              ? selectedResumes.length === 1
                ? "Uploading Resume..."
                : "Uploading Resumes..."
              : downloading
                ? "Downloading template..."
                : "Loading..."
        }
      />
      <Alert
        show={!!state.error}
        title="Error"
        message={state.error || ""}
        type="error"
        onClose={closeAlert}
        primaryButtonText="OK"
      />
      <div className="upload-page-wrapper">
        <motion.div
          className="learner-page"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.25 }}
        >
          {/* ── Hero ── */}
          <div className="up-hero">
            <div className="up-hero-left">
              <button
                className="up-back-btn"
                onClick={() => navigate(returnUrl)}
              >
                <svg
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 12H5m7-7l-7 7 7 7"
                  />
                </svg>
              </button>
              <h1>Upload Resumes</h1>
            </div>
          </div>

          {/* ─────────────── EXCEL MODE ─────────────── */}
          {mode === "excel" && (
            <>
              {/* ── Upload & Download Section ── */}
              <div className="up-split">
                <div className="up-split-left">
                  <div
                    className="up-drop-wrapper"
                    style={
                      dragOver
                        ? { borderColor: "#059669", background: "#f0fdf4" }
                        : {}
                    }
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                  >
                    <div className="up-drop-icon">
                      <svg
                        width="24"
                        height="24"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                    </div>
                    <h3>
                      {uploading
                        ? "Uploading your file..."
                        : "Select a CSV / XLSX file to upload Applicants"}
                    </h3>
                    <p>
                      {uploading
                        ? "Please wait while we process your file."
                        : "or drag and drop your file here"}
                    </p>
                    <div className="up-drop-actions">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileInputChange}
                        accept=".xlsx,.xls,.csv"
                        style={{ display: "none" }}
                      />
                      <button
                        className="btn-primary"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                      >
                        <svg
                          width="16"
                          height="16"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 13l-3-3m0 0l-3 3m3-3v12M3.293 6.293A1 1 0 014 6h16a1 1 0 011 1v1a1 1 0 01-1 1H4a1 1 0 01-1-1V7a1 1 0 01.293-.707z"
                          />
                        </svg>
                        {uploading ? "Uploading…" : "Select File"}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="up-split-right">
                  <div className="up-template-card">
                    <div className="up-template-icon">
                      <svg
                        width="22"
                        height="22"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </div>
                    <h3>Download Template</h3>
                    <p>Get the standard template for upload</p>
                    <div className="up-template-wrapper" ref={templateRef}>
                      <button
                        className="btn-primary"
                        onClick={() =>
                          setShowTemplateOptions(!showTemplateOptions)
                        }
                      >
                        <svg
                          width="16"
                          height="16"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                          />
                        </svg>
                        Download Template
                        <svg
                          width="14"
                          height="14"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          style={{ marginLeft: 2 }}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                      {showTemplateOptions && (
                        <div className="up-template-dropdown">
                          <button
                            onClick={() => handleDownloadTemplate("excel")}
                          >
                            <svg
                              width="16"
                              height="16"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                            Excel (.xlsx)
                          </button>
                          <button onClick={() => handleDownloadTemplate("csv")}>
                            <svg
                              width="16"
                              height="16"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                            CSV (.csv)
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Excel Upload Tracker ── */}
              <div className="up-tracker-panel">
                <div className="up-tracker-head">
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 10 }}
                  >
                    <h2>Upload Tracker</h2>
                    <span className="up-stat-chip total">
                      {records.length} Total
                    </span>
                    <span className="up-stat-chip success">
                      {successCount} Success
                    </span>
                    <span className="up-stat-chip processing">
                      {processingCount} Processing
                    </span>
                    <span className="up-stat-chip failed">
                      {failedCount} Failed
                    </span>
                    {/* <button
                      onClick={fetchUploadRecords}
                      style={{
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        color: "#94a3b8",
                        display: "flex",
                        alignItems: "center",
                      }}
                      title="Refresh"
                    >
                      <svg
                        width="18"
                        height="18"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                    </button> */}
                  </div>
                  <div className="up-tracker-head-right">
                    <div
                      className="lp-search"
                      style={{ width: 200, position: "relative" }}
                    >
                      <svg
                        width="14"
                        height="14"
                        fill="none"
                        stroke="#94a3b8"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                      <input
                        placeholder="Search files..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                          fontSize: "0.8rem",
                          paddingRight: searchQuery ? "30px" : "10px",
                        }}
                      />
                      {searchQuery && (
                        <button
                          style={{
                            position: "absolute",
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            border: "none",
                            background: "#f1f5f9",
                            cursor: "pointer",
                            color: "#64748b",
                            width: "20px",
                            height: "20px",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0,
                          }}
                          onClick={() => setSearchQuery("")}
                          title="Clear Search"
                        >
                          <svg
                            width="12"
                            height="12"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2.5}
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      style={{
                        height: 40,
                        padding: "0 12px",
                        border: "1px solid #e2e8f0",
                        borderRadius: 20,
                        fontSize: "0.82rem",
                        background: "#fff",
                        color: "#334155",
                        cursor: "pointer",
                        outline: "none",
                      }}
                    >
                      <option value="all">All Status</option>
                      <option value="success">Success</option>
                      <option value="processing">Processing</option>
                      <option value="failed">Failed</option>
                    </select>
                  </div>
                </div>

                <div className="lp-table-scroll-area">
                  {records.length === 0 ? (
                    <div className="table-empty">
                      <svg
                        width="40"
                        height="40"
                        fill="none"
                        stroke="#cbd5e1"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <span>No upload records found</span>
                      {(searchQuery || statusFilter !== "all") && (
                        <span style={{ fontSize: "0.8rem" }}>
                          Try adjusting your filters
                        </span>
                      )}
                    </div>
                  ) : (
                    <Table className="learner-table">
                      <TableHeader>
                        <TableRow style={{ borderBottom: "1px solid #e2e8f0" }}>
                          <TableHead style={{ textAlign: "left" }}>
                            File Name
                          </TableHead>
                          <TableHead style={{ textAlign: "left" }}>
                            Status
                          </TableHead>
                          <TableHead style={{ textAlign: "left" }}>
                            Timestamp
                          </TableHead>
                          <TableHead style={{ textAlign: "left" }}>
                            Uploaded By
                          </TableHead>
                          <TableHead
                            className="actions-header"
                            style={{ textAlign: "left" }}
                          >
                            Action
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {records.map((row: any, i: number) => (
                          <TableRow
                            key={row.file_id || i}
                            style={{ borderBottom: "1px solid #e2e8f0" }}
                          >
                            <TableCell style={{ textAlign: "left" }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "flex-start",
                                  gap: 8,
                                }}
                              >
                                <svg
                                  width="20"
                                  height="20"
                                  fill="none"
                                  stroke="#059669"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                  />
                                </svg>
                                <span
                                  style={{
                                    fontWeight: 600,
                                    color: "#334155",
                                    fontSize: "0.85rem",
                                  }}
                                >
                                  {row.file_name || "—"}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell style={{ textAlign: "left" }}>
                              {getTrackerPill(row.status, row.status_note)}
                            </TableCell>
                            <TableCell style={{ textAlign: "left" }}>
                              <span
                                style={{
                                  color: "#64748b",
                                  fontSize: "0.82rem",
                                  fontWeight: 500,
                                }}
                              >
                                {row.upload_time || "—"}
                              </span>
                            </TableCell>
                            <TableCell style={{ textAlign: "left" }}>
                              <span
                                style={{
                                  color: "#334155",
                                  fontSize: "0.85rem",
                                  fontWeight: 500,
                                }}
                              >
                                {row.uploaded_by || "—"}
                              </span>
                            </TableCell>
                            <TableCell
                              className="actions-cell"
                              style={{ textAlign: "left" }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "flex-start",
                                }}
                              >
                                <button
                                  className="action-btn view"
                                  onClick={() => handleViewStatus(row)}
                                  title="View Details"
                                >
                                  {(row.status || "")
                                    .toLowerCase()
                                    .includes("fail") ||
                                    (row.status || "")
                                      .toLowerCase()
                                      .includes("error") ? (
                                    <svg
                                      width="16"
                                      height="16"
                                      fill="none"
                                      stroke="#ef4444"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                      />
                                    </svg>
                                  ) : (
                                    <svg
                                      width="16"
                                      height="16"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                      />
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                      />
                                    </svg>
                                  )}
                                </button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </div>
            </>
          )}

          {/* ─────────────── RESUME MODE ─────────────── */}
          {mode === "resume" && (
            <>
              {/* ── Resume Upload Zone ── */}
              <div
                className="up-upload-bar"
                onDragOver={(e) => {
                  e.preventDefault();
                  setResumeDragOver(true);
                }}
                onDragLeave={() => setResumeDragOver(false)}
                onDrop={handleResumeDrop}
                style={{
                  background: resumeDragOver
                    ? "#f0fdf4"
                    : "linear-gradient(180deg, rgba(5,150,105,0.03) 0%, transparent 100%)",
                  borderColor: resumeDragOver ? "#059669" : "",
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0,
                  padding: '36px 32px 28px',
                  cursor: 'pointer',
                }}
                onClick={() => !resumeUploading && resumeFileInputRef.current?.click()}
              >
                <input
                  type="file"
                  ref={resumeFileInputRef}
                  onChange={handleResumeFileInputChange}
                  accept=".pdf,.docx"
                  multiple
                  style={{ display: "none" }}
                />

                {/* Upload Icon */}
                <div style={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #d1fae5, #a7f3d0)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 14,
                  boxShadow: '0 4px 12px rgba(5,150,105,0.15)',
                }}>
                  <svg width="26" height="26" fill="none" stroke="#059669" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L5 8m4-4v12" />
                  </svg>
                </div>

                <div style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>
                  {resumeUploading ? 'Uploading resumes...' : 'Drop your resumes here'}
                </div>
                <div style={{ fontSize: '0.82rem', color: '#94a3b8', marginBottom: 16 }}>
                  {resumeUploading ? 'Please wait...' : 'PDF or DOCX · Multiple files supported'}
                </div>

                {/* Selected file pills */}
                {selectedResumes.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 4 }}>
                    {selectedResumes.map((file, idx) => (
                      <span key={idx} style={{
                        fontSize: '0.72rem',
                        background: '#f1f5f9',
                        borderRadius: 20,
                        padding: '4px 10px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                        color: '#475569',
                        border: '1px solid #e2e8f0',
                      }}>
                        <svg width="11" height="11" fill="none" stroke="#059669" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        {file.name}
                        <button
                          onClick={(e) => { e.stopPropagation(); removeResumeFile(idx); }}
                          style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#94a3b8', padding: 0, display: 'flex', lineHeight: 1 }}
                          title="Remove"
                        >
                          <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* ── Settings Row ── */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px',
                marginTop: '16px',
              }}>
                {/* Link to Job */}
                <div style={{
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '16px 18px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}>
                  <label style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#059669',
                    textTransform: 'uppercase',
                    letterSpacing: '0.6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                  }}>
                    <Link2 size={12} />
                    Link to Job Opportunity
                    <span style={{ color: '#94a3b8', fontWeight: 500, textTransform: 'none', letterSpacing: 0, fontSize: '11px' }}>(Optional)</span>
                  </label>
                  <div style={{ position: 'relative' }}>
                    <select
                      value={selectedJobId}
                      onChange={(e) => setSelectedJobId(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '9px 12px 9px 34px',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13.5px',
                        fontWeight: 500,
                        color: '#1e293b',
                        background: '#f8fafc',
                        cursor: 'pointer',
                        outline: 'none',
                        appearance: 'none',
                        WebkitAppearance: 'none',
                      }}
                    >
                      <option value="">Do not link to a specific job</option>
                      {jobs.map(job => (
                        <option key={job.opportunity_id} value={job.opportunity_id}>{job.title}</option>
                      ))}
                    </select>
                    <Link2 size={14} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                  </div>
                </div>

                {/* Applicant Source */}
                <div style={{
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '16px 18px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}>
                  <label style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#059669',
                    textTransform: 'uppercase',
                    letterSpacing: '0.6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                  }}>
                    <Globe size={12} />
                    Applicant Source
                  </label>
                  <div style={{ position: 'relative' }}>
                    <select
                      value={selectedSource}
                      onChange={(e) => setSelectedSource(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '9px 12px 9px 34px',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13.5px',
                        fontWeight: 500,
                        color: '#1e293b',
                        background: '#f8fafc',
                        cursor: 'pointer',
                        outline: 'none',
                        appearance: 'none',
                        WebkitAppearance: 'none',
                      }}
                    >
                      {APPLICANT_SOURCES.map(source => (
                        <option key={source} value={source}>{source}</option>
                      ))}
                    </select>
                    <Globe size={14} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                  </div>
                </div>
              </div>

              {/* ── Action Buttons ── */}
              <div style={{ display: 'flex', gap: 10, marginTop: '14px' }}>
                <button
                  onClick={() => resumeFileInputRef.current?.click()}
                  disabled={resumeUploading}
                  style={{
                    height: 40,
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    padding: '0 22px',
                    borderRadius: 10,
                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                    color: '#fff',
                    border: 'none',
                    cursor: resumeUploading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 7,
                    boxShadow: '0 3px 10px rgba(5,150,105,0.2)',
                    transition: 'all 0.2s',
                    whiteSpace: 'nowrap',
                    opacity: resumeUploading ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => { if (!resumeUploading) { e.currentTarget.style.boxShadow = '0 5px 16px rgba(5,150,105,0.3)'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 3px 10px rgba(5,150,105,0.2)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13l-3-3m0 0l-3 3m3-3v12M3.293 6.293A1 1 0 014 6h16a1 1 0 011 1v1a1 1 0 01-1 1H4a1 1 0 01-1-1V7a1 1 0 01.293-.707z" />
                  </svg>
                  {selectedResumes.length > 0 ? `${selectedResumes.length} File${selectedResumes.length > 1 ? 's' : ''} Selected` : 'Select File'}
                </button>

                {selectedResumes.length > 0 && (
                  <button
                    onClick={() => handleResumeUpload()}
                    disabled={resumeUploading}
                    style={{
                      height: 40,
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      padding: '0 22px',
                      borderRadius: 10,
                      background: '#1e293b',
                      color: '#fff',
                      border: 'none',
                      cursor: resumeUploading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 7,
                      transition: 'all 0.2s',
                      whiteSpace: 'nowrap',
                      opacity: resumeUploading ? 0.6 : 1,
                    }}
                    onMouseEnter={(e) => { if (!resumeUploading) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(30,41,59,0.25)'; } }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L5 8m4-4v12" />
                    </svg>
                    Upload ({selectedResumes.length})
                  </button>
                )}
              </div>

              {/* ── Resume Upload Tracker ── */}
              <div className="up-tracker-panel">
                <div className="up-tracker-head">
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 10 }}
                  >
                    <h2>Resume Upload Tracker</h2>
                    <span className="up-stat-chip total">
                      {filteredResumeRecords.length} Total
                    </span>
                    <button
                      onClick={() => {
                        if (resumePage === 1) {
                          fetchResumeUploadRecords(1, false);
                        } else {
                          setResumePage(1);
                        }
                      }}
                      style={{
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        color: "#94a3b8",
                        display: "flex",
                        alignItems: "center",
                      }}
                      title="Refresh"
                    >
                      <svg
                        width="18"
                        height="18"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                    </button>
                  </div>
                  <div className="up-tracker-head-right">
                    <div
                      className="lp-search"
                      style={{ width: 200, position: "relative" }}
                    >
                      <svg
                        width="14"
                        height="14"
                        fill="none"
                        stroke="#94a3b8"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                      <input
                        placeholder="Search files..."
                        value={resumeSearchQuery}
                        onChange={(e) => setResumeSearchQuery(e.target.value)}
                        style={{
                          fontSize: "0.8rem",
                          paddingRight: resumeSearchQuery ? "30px" : "10px",
                        }}
                      />
                      {resumeSearchQuery && (
                        <button
                          style={{
                            position: "absolute",
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            border: "none",
                            background: "#f1f5f9",
                            cursor: "pointer",
                            color: "#64748b",
                            width: "20px",
                            height: "20px",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0,
                          }}
                          onClick={() => setResumeSearchQuery("")}
                        >
                          <svg
                            width="12"
                            height="12"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2.5}
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                    <StatusFilterDropdown
                      value={resumeStatusFilter}
                      allLabel="All Status"
                      placeholder="Filter by batch status"
                      options={RESUME_STATUS_FILTER_OPTIONS}
                      onChange={setResumeStatusFilter}
                    />
                  </div>
                </div>

                <div className="lp-table-scroll-area">
                  {filteredResumeRecords.length === 0 ? (
                    <div className="table-empty">
                      <svg
                        width="40"
                        height="40"
                        fill="none"
                        stroke="#cbd5e1"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <span>No resume upload records found</span>
                      {(resumeSearchQuery || resumeStatusFilter !== "all") && (
                        <span style={{ fontSize: "0.8rem" }}>
                          Try adjusting your filters
                        </span>
                      )}
                    </div>
                  ) : (
                    <Table className="learner-table">
                      <TableHeader>
                        <TableRow style={{ borderBottom: "1px solid #e2e8f0" }}>
                          <TableHead style={{ textAlign: "left" }}>
                            Batch Name
                          </TableHead>
                          <TableHead style={{ textAlign: "left" }}>
                            Status
                          </TableHead>
                          <TableHead style={{ textAlign: "left" }}>
                            Progress
                          </TableHead>
                          <TableHead style={{ textAlign: "left" }}>
                            Timestamp
                          </TableHead>
                          <TableHead
                            className="actions-header"
                            style={{ textAlign: "left" }}
                          >
                            Action
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredResumeRecords.map((row, i) => (
                          <TableRow
                            key={row.file_id || i}
                            style={{ borderBottom: "1px solid #e2e8f0" }}
                          >
                            <TableCell style={{ textAlign: "left" }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "flex-start",
                                  gap: 8,
                                }}
                              >
                                <svg
                                  width="20"
                                  height="20"
                                  fill="none"
                                  stroke="#059669"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                                  />
                                </svg>
                                <span
                                  style={{
                                    fontWeight: 600,
                                    color: "#334155",
                                    fontSize: "0.85rem",
                                  }}
                                >
                                  {row.file_name || "—"}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell style={{ textAlign: "left" }}>
                              {getTrackerPill(row.status, row.status_note)}
                            </TableCell>
                            <TableCell style={{ textAlign: "left" }}>
                              <span
                                style={{
                                  color: "#64748b",
                                  fontSize: "0.82rem",
                                  fontWeight: 500,
                                }}
                              >
                                {row.processed_resumes || 0}/
                                {row.total_resumes || 0}
                                {row.failed_resumes > 0 && (
                                  <span
                                    style={{ color: "#ef4444", marginLeft: 4 }}
                                  >
                                    ({row.failed_resumes} failed)
                                  </span>
                                )}
                              </span>
                            </TableCell>
                            <TableCell style={{ textAlign: "left" }}>
                              <span
                                style={{
                                  color: "#64748b",
                                  fontSize: "0.82rem",
                                  fontWeight: 500,
                                }}
                              >
                                {row.upload_time || "—"}
                              </span>
                            </TableCell>
                            <TableCell
                              className="actions-cell"
                              style={{ textAlign: "left" }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "flex-start",
                                }}
                              >
                                <button
                                  className="action-btn view"
                                  onClick={() => handleResumeViewStatus(row)}
                                  title="View Details"
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                    />
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                    />
                                  </svg>
                                </button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                  {filteredResumeRecords.length > 0 && resumeHasMore && (
                    <div ref={lastResumeElementRef} style={{ height: "20px", width: "100%" }} />
                  )}
                </div>
              </div>
            </>
          )}
        </motion.div>
      </div>

      {/* ── Excel Detail Modal ── */}
      <Modal
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        title={`Upload Details: ${selectedFileName}`}
        width="800px"
      >
        <div
          style={{
            padding: "16px 20px",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <div
            className="lp-search"
            style={{ width: "100%", position: "relative" }}
          >
            <svg
              width="14"
              height="14"
              fill="none"
              stroke="#94a3b8"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              placeholder="Search by name or email..."
              value={viewSearchQuery}
              onChange={(e) => setViewSearchQuery(e.target.value)}
              style={{
                fontSize: "0.85rem",
                paddingRight: viewSearchQuery ? "30px" : "10px",
              }}
            />
            {viewSearchQuery && (
              <button
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  border: "none",
                  background: "#f1f5f9",
                  cursor: "pointer",
                  color: "#64748b",
                  width: "20px",
                  height: "20px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0,
                }}
                onClick={() => setViewSearchQuery("")}
              >
                <svg
                  width="12"
                  height="12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
          <div className="lp-table-scroll-area" style={{ maxHeight: "420px" }}>
            {viewLoading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "40px",
                }}
              >
                <div className="table-loading-spinner" />
              </div>
            ) : filteredViewRecords.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  color: "#94a3b8",
                  padding: "40px",
                }}
              >
                {viewSearchQuery
                  ? "No matching records found."
                  : "No records found for this file."}
              </div>
            ) : (
              <Table
                className="learner-table"
                style={{ border: "1px solid #e2e8f0" }}
              >
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email Address</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Educational Qualification</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredViewRecords.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <span style={{ fontWeight: 600, color: "#334155" }}>
                          {r.first_name} {r.last_name}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span style={{ color: "#64748b" }}>
                          {r.email_address}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span style={{ color: "#64748b" }}>
                          {r.phone || "—"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span style={{ color: "#64748b" }}>
                          {r.education || "—"}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </Modal>

      {/* ── Resume Detail Modal ── */}
      <Modal
        open={resumeDetailOpen}
        onClose={() => {
          setResumeDetailOpen(false);
          fetchResumeUploadRecords();
        }}
        title={`Resume Upload Details: ${selectedResumeFileName}`}
        width="900px"
        headerExtra={
          <button
            onClick={handleResumeDetailRefresh}
            disabled={resumeDetailLoading}
            title="Refresh status"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "6px 14px",
              borderRadius: "8px",
              fontSize: "13px",
              fontWeight: 600,
              color: "#2563eb",
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              cursor: resumeDetailLoading ? "not-allowed" : "pointer",
              opacity: resumeDetailLoading ? 0.6 : 1,
            }}
          >
            <svg
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              style={{
                animation: resumeDetailLoading
                  ? "spin 0.8s linear infinite"
                  : "none",
              }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Refresh
          </button>
        }
      >
        <div
          style={{
            padding: "16px 20px",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <div className="lp-table-scroll-area" style={{ maxHeight: "420px" }}>
            {resumeDetailLoading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "40px",
                }}
              >
                <div className="table-loading-spinner" />
              </div>
            ) : resumeDetailData.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  color: "#94a3b8",
                  padding: "40px",
                }}
              >
                No records found for this batch.
              </div>
            ) : (
              <Table
                className="learner-table"
                style={{ border: "1px solid #e2e8f0" }}
              >
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Filename</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Extracted Name</TableHead>
                    <TableHead>Extracted Email</TableHead>
                    <TableHead>Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {resumeDetailData.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <span style={{ color: "#64748b", fontSize: "0.82rem" }}>
                          {r.resume_index + 1}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span
                          style={{
                            fontWeight: 600,
                            color: "#334155",
                            fontSize: "0.85rem",
                          }}
                        >
                          {r.original_filename}
                        </span>
                      </TableCell>
                      <TableCell>
                        {getResumeStatusPill(r.status, r.error_message)}
                      </TableCell>
                      <TableCell>
                        <span style={{ color: "#334155", fontSize: "0.85rem" }}>
                          {r.extracted_name || "—"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span style={{ color: "#64748b", fontSize: "0.85rem" }}>
                          {r.extracted_email || "—"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span style={{ color: "#ef4444", fontSize: "0.8rem" }}>
                          {r.error_message || "—"}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </Modal>
      <Alert
        show={!!infoMessage}
        title="Upload Successful"
        message={infoMessage || ""}
        type="info"
        onClose={() => setInfoMessage(null)}
      />
    </>
  );
};

export default UploadLearnersPage;
